import * as React from 'react'
import { SelectableCard } from '@bnsn/ui/atoms/selectable-card'
import { cn } from '@bnsn/ui/lib/utils'

export interface ModeCardProps {
  /**
   * The title of the mode (e.g., "EASY MODE", "PRO MODE")
   */
  title: string
  /**
   * The description text for the mode
   */
  description: string
  /**
   * The label text for the pill (e.g., "Recommended for Beginners")
   */
  label: string
  /**
   * The icon component to display
   */
  icon: React.ReactNode
  /**
   * Whether the card is currently selected
   */
  isSelected?: boolean
  /**
   * Callback when the card is selected
   */
  onSelect?: () => void
  /**
   * Whether the card is disabled
   */
  disabled?: boolean
  /**
   * Additional CSS classes
   */
  className?: string
}

/**
 * A specialized card component for displaying mode selection options with consistent branding.
 *
 * This component is built on top of SelectableCard and provides a standardized layout for
 * mode selection interfaces. It features:
 * - Centered layout with generous padding
 * - Brand-consistent color scheme using HSL color values
 * - Icon display in a circular background
 * - Hierarchical text layout (title → description → label)
 * - 4:3 aspect ratio for consistent grid layouts
 *
 * @example
 * ```tsx
 * <ModeCard
 *   title="EASY MODE"
 *   description="Perfect for beginners with guided workflows"
 *   label="Recommended for Beginners"
 *   icon={<StarIcon className="w-8 h-8 text-white" />}
 *   isSelected={mode === 'easy'}
 *   onSelect={() => setMode('easy')}
 * />
 * ```
 */
const ModeCard = React.forwardRef<HTMLDivElement, ModeCardProps>(
  ({ title, description, label, icon, isSelected = false, onSelect, disabled = false, className }, ref) => {
    return (
      <SelectableCard
        ref={ref}
        className={cn('p-10 text-center aspect-[4/3]', className)}
        isSelected={isSelected}
        onSelect={onSelect}
        disabled={disabled}
      >
        {/* Icon Container - Circular background with brand color */}
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-6">{icon}</div>

        {/* Mode Title - Large, bold text in brand color */}
        <h2 className="text-2xl font-bold text-primary mb-4">{title}</h2>

        {/* Mode Description - Subtle text with proper spacing */}
        <p className="text-slate-600 mb-6 text-base">{description}</p>

        {/* Label Pill - Light background with darker text for contrast */}
        <div className="inline-block bg-blue-50 text-primary px-4 py-1.5 rounded-full text-sm font-semibold">{label}</div>
      </SelectableCard>
    )
  },
)
ModeCard.displayName = 'ModeCard'

export { ModeCard }
